const BASE_URL = import.meta.env.VITE_API_URL ?? '';

type TokenGetter = () => string | null;
type TokenRefresher = () => Promise<string | null>;

let getToken: TokenGetter = () => null;
let refreshAccessToken: TokenRefresher = async () => null;
let pendingRefresh: Promise<string | null> | null = null;

export function configureClient(
  tokenGetter: TokenGetter,
  tokenRefresher: TokenRefresher,
): void {
  getToken = tokenGetter;
  refreshAccessToken = tokenRefresher;
}

class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  retry = true,
  overrideToken?: string,
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  // On a post-refresh retry, use the token the refresh just returned instead
  // of re-reading getToken() — React may not have re-rendered AuthContext
  // (and re-registered the closure via configureClient) yet, so getToken()
  // can still return the token that just got us the 401 in the first place.
  const token = overrideToken ?? getToken();

  const headers: Record<string, string> = {};

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (response.status === 401 && retry && !path.includes('/auth/')) {
    if (!pendingRefresh) {
      pendingRefresh = refreshAccessToken().finally(() => { pendingRefresh = null; });
    }
    const newToken = await pendingRefresh;
    if (newToken) {
      return request<T>(method, path, body, false, newToken);
    }
    throw new ApiError(401, 'Sessão expirada. Faça login novamente.');
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => response.statusText);
    throw new ApiError(response.status, errorBody);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export function get<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body);
}

export function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, body);
}

export function del<T>(path: string): Promise<T> {
  return request<T>('DELETE', path);
}

export { ApiError };
